import type { ComponentType, ReactNode } from "react";
import { cn } from "../lib/cn";
import { CheckCircleIcon, ClockIcon, InfoCircleIcon, SparkleIcon, XIcon } from "./icons";

export type AlertTone = "info" | "success" | "warning" | "error" | "brand";

export interface AlertProps {
  tone: AlertTone;
  title: string;
  /** Optional body copy rendered under the title. */
  children?: ReactNode | undefined;
  /** When set, renders a dismiss button that calls it. */
  onDismiss?: (() => void) | undefined;
  className?: string | undefined;
}

interface ToneSpec {
  container: string;
  text: string;
  chip: string;
  Icon: ComponentType<{ size: number; className?: string | undefined }>;
}

const tones: Record<AlertTone, ToneSpec> = {
  info: {
    container: "bg-info-bg border-info-border",
    text: "text-info",
    chip: "bg-info/9",
    Icon: InfoCircleIcon,
  },
  success: {
    container: "bg-success-bg border-success-border",
    text: "text-success",
    chip: "bg-success/9",
    Icon: CheckCircleIcon,
  },
  warning: {
    container: "bg-warning-bg border-warning-border",
    text: "text-warning",
    chip: "bg-warning/9",
    Icon: ClockIcon,
  },
  error: {
    container: "bg-danger-bg border-danger-border",
    text: "text-danger",
    chip: "bg-danger/9",
    Icon: XIcon,
  },
  brand: {
    container: "bg-cream border-[#fdba74]",
    text: "text-ember",
    chip: "bg-ember/9",
    Icon: SparkleIcon,
  },
};

/** Alert banner with a tone icon, a title, an optional body and an optional dismiss button. */
export function Alert({ tone, title, children, onDismiss, className }: AlertProps): ReactNode {
  const { container, text, chip, Icon } = tones[tone];
  return (
    <div
      role="alert"
      className={cn("flex items-start gap-4 rounded-2xl border px-5 py-4", container, className)}
    >
      <span
        className={cn(
          "mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full",
          text,
          chip,
        )}
      >
        <Icon size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-foreground text-base font-bold">{title}</p>
        {children != null && (
          <div className="text-foreground mt-0.5 text-[15px] leading-[1.625]">{children}</div>
        )}
      </div>
      {onDismiss && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className={cn(
            "mt-0.5 inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full",
            text,
          )}
        >
          <XIcon size={14} />
        </button>
      )}
    </div>
  );
}
